import {
  Fragment,
  useRef,
  useEffect,
} from 'react';
import classNames from 'classnames';
import type {
  ImageEntity,
  PriceDropEntity,
} from '@dinosave/core/types';
import {
  formatPrice,
} from '@dinosave/core/utils';
import Button from '@/ui/components/Button';
import Skeleton from '@/ui/components/Skeleton';
import Text from '@/ui/components/Text';
import {
  ArrowRightIcon,
  ArrowUpRightIcon,
} from '@/ui/icons';
import dominantColor from '@/utils/dominantColor';

import styles from './PriceDrop.module.css';

type PriceDropProps = {
  priceDrop?: PriceDropEntity;
  skeleton?: boolean;
}

const PriceDrop = (props: PriceDropProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    priceDrop,
    skeleton = false,
  } = props;

  const {
    item,
    previousPrice,
    currentPrice,
    currency,
    viewedAt,
  } = priceDrop || {};

  const {
    url,
    image,
  } = item || {};
  const itemImage = image as ImageEntity;

  // ##########################################################################
  // STATE
  // ##########################################################################

  const mediaRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // ##########################################################################
  // CONSTANTS
  // ##########################################################################

  const storeUrl = url ? new URL(url).hostname.replace(/^www\d?\./, '') : '';
  const refUrl = `/i/${item?.slug}`;
  const imageUrl = itemImage?.small?.url;

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const setDominantBackground = () => {
    const background = dominantColor(imageRef.current);
    mediaRef.current.style.background = background;
  };

  // ##########################################################################
  // EFFECTS
  // ##########################################################################

  useEffect(() => {
    const image = imageRef.current;

    if (
      !image
    ) return;

    const isLoaded = image.src && image.complete;

    if (isLoaded) {
      setDominantBackground();
    } else {
      image.addEventListener('load', setDominantBackground);
    }

    return () => {
      image.removeEventListener('load', setDominantBackground);
    };
  }, []);

  // ##########################################################################
  // COMPUTED STYLES
  // ##########################################################################

  const priceDropClassName = classNames({
    [styles.priceDrop]: true,
    [styles.viewed]: viewedAt,
    [styles.skeleton]: skeleton,
  });

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <a
      href={refUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={priceDropClassName}
    >
      <div
        ref={mediaRef}
        className={styles.media}
      >
        {skeleton ? (
          <div className={styles.mediaSkeleton}>
            <Skeleton />
          </div>
        ) : (
          <img
            ref={imageRef}
            src={imageUrl}
            alt="Image"
            crossOrigin="anonymous"
          />
        )}
      </div>

      <div className={styles.details}>
        <div className={styles.sub}>
          <div className={styles.store}>
            {skeleton ? (
              <div className={styles.storeSkeleton}>
                <Skeleton />
              </div>
            ) : (
              <Text weight="500">
                {storeUrl}
              </Text>
            )}
          </div>

          <div className={styles.indicator} />
        </div>

        <div className={styles.name}>
          {skeleton ? (
            <Fragment>
              <div className={styles.nameSkeleton}>
                <Skeleton />
              </div>

              <div className={styles.nameSkeleton}>
                <Skeleton />
              </div>
            </Fragment>
          ) : (
            <Text
              kind="paragraph"
              size="regular"
              weight="600"
            >
              {item.name}
            </Text>
          )}
        </div>

        <div className={styles.price}>
          <div className={styles.previousPrice}>
            {skeleton ? (
              <div className={styles.priceSkeleton}>
                <Skeleton />
              </div>
            ) : (
              <Text
                kind="caption"
                size="regular"
                color="neutral-500"
              >
                {formatPrice(previousPrice, currency)}
              </Text>
            )}
          </div>

          <div className={styles.divider}>
            <ArrowRightIcon />
          </div>

          <div className={styles.currentPrice}>
            {skeleton ? (
              <div className={styles.priceSkeleton}>
                <Skeleton />
              </div>
            ) : (
              <Text
                kind="paragraph"
                size="regular"
                color="primary-600"
                weight="600"
              >
                {formatPrice(currentPrice, currency)}
              </Text>
            )}
          </div>
        </div>
      </div>
    </a>
  );
};

export default PriceDrop;
